/**
 * <pdp-ingredient-spotlight> — ingredient names that switch a photo and a description.
 *
 * Desktop (1025px and up): hovering, focusing or clicking a name activates it. When the pointer
 *                          (or focus) leaves the names, the first one becomes the active one again.
 * Mobile and tablet:       the names are a horizontally scrollable tab row; tapping one
 *                          activates it and scrolls it to the centre of the row. The
 *                          first time the row scrolls into view it nudges 20px to the
 *                          right and back so it reads as scrollable.
 *
 * Activating only moves `is-active` between the tab, figure and detail that share an
 * index; CSS does the showing, hiding and fading.
 *
 * Markup contract (sections/pdp-ingredient-spotlight.liquid):
 *   <pdp-ingredient-spotlight>
 *     <figure data-spotlight-figure="0"> …
 *     <div data-spotlight-scroller> <ul data-spotlight-list> <button data-spotlight-tab="0"> …
 *     <div data-spotlight-detail="0"> …
 *   </pdp-ingredient-spotlight>
 */
(function () {
  if (customElements.get('pdp-ingredient-spotlight')) return;

  const ACTIVE_CLASS = 'is-active';
  const HINT_DISTANCE = 20;
  const HINT_DURATION = 1000;
  // Crossing the gap between two names is not leaving the list.
  const RESET_DELAY = 150;

  class PdpIngredientSpotlight extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-spotlight-scroller]');
      this.list = this.querySelector('[data-spotlight-list]');
      this.tabs = Array.from(this.querySelectorAll('[data-spotlight-tab]'));
      if (!this.scroller || !this.list || this.tabs.length === 0) return;

      this.mobileQuery = window.matchMedia('(width < 1025px)');
      this.activeIndex = 0;
      this.resetTimer = null;

      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.list.addEventListener('pointerover', this.onPointerOver);
      this.list.addEventListener('pointerout', this.onPointerOut);
      this.list.addEventListener('focusin', this.onFocusIn);
      this.list.addEventListener('focusout', this.onFocusOut);
      this.list.addEventListener('click', this.onClick);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.observeHint();
    }

    disconnectedCallback() {
      if (!this.list) return;

      this.list.removeEventListener('pointerover', this.onPointerOver);
      this.list.removeEventListener('pointerout', this.onPointerOut);
      this.list.removeEventListener('focusin', this.onFocusIn);
      this.list.removeEventListener('focusout', this.onFocusOut);
      this.list.removeEventListener('click', this.onClick);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.hintObserver) this.hintObserver.disconnect();
      if (this.hintAnimation) this.hintAnimation.cancel();
      window.clearTimeout(this.resetTimer);
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    indexOfTab(element) {
      const tab = element.closest('[data-spotlight-tab]');
      return tab ? this.tabs.indexOf(tab) : -1;
    }

    onPointerOver(event) {
      if (this.isMobile() || event.pointerType !== 'mouse') return;
      if (this.indexOfTab(event.target) < 0) return;

      window.clearTimeout(this.resetTimer);
      this.activate(this.indexOfTab(event.target));
    }

    onPointerOut(event) {
      if (this.isMobile() || event.pointerType !== 'mouse') return;
      if (this.indexOfTab(event.target) < 0) return;
      if (event.relatedTarget && this.indexOfTab(event.relatedTarget) >= 0) return;

      this.scheduleReset();
    }

    onFocusIn(event) {
      if (this.isMobile()) return;

      window.clearTimeout(this.resetTimer);
      this.activate(this.indexOfTab(event.target));
    }

    onFocusOut(event) {
      if (this.isMobile()) return;
      if (event.relatedTarget && this.list.contains(event.relatedTarget)) return;

      this.scheduleReset();
    }

    scheduleReset() {
      window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => this.activate(0), RESET_DELAY);
    }

    onClick(event) {
      const index = this.indexOfTab(event.target);
      if (index < 0) return;

      this.activate(index);
      if (this.isMobile()) this.centerTab(index);
    }

    onBlockSelect(event) {
      const item = event.target.closest('li');
      const tab = item ? item.querySelector('[data-spotlight-tab]') : null;
      const index = tab ? this.tabs.indexOf(tab) : -1;
      if (index < 0) return;

      this.activate(index);
      if (this.isMobile()) this.centerTab(index);
    }

    centerTab(index) {
      const tab = this.tabs[index];
      if (!tab) return;

      const tabRect = tab.getBoundingClientRect();
      const scrollerRect = this.scroller.getBoundingClientRect();
      const offset = tabRect.left + tabRect.width / 2 - (scrollerRect.left + this.scroller.clientWidth / 2);
      this.scroller.scrollTo({ left: this.scroller.scrollLeft + offset, behavior: 'smooth' });
    }

    activate(index) {
      if (index < 0 || index >= this.tabs.length || index === this.activeIndex) return;

      this.activeIndex = index;
      this.tabs.forEach((tab, position) => {
        const isActive = position === index;
        tab.classList.toggle(ACTIVE_CLASS, isActive);
        if (isActive) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });

      this.querySelectorAll('[data-spotlight-figure], [data-spotlight-detail]').forEach((element) => {
        const position = Number(element.dataset.spotlightFigure ?? element.dataset.spotlightDetail);
        element.classList.toggle(ACTIVE_CLASS, position === index);
      });
    }

    /**
     * One-off nudge when the tab row first scrolls into view: the names slide 20px
     * to the right and settle back. It plays on the list (not the scroller), so it
     * never changes the scroll position the visitor may already have set.
     */
    observeHint() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || typeof IntersectionObserver === 'undefined' || !this.list.animate) return;

      this.hintObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.hintObserver.disconnect();
          this.playHint();
        },
        { threshold: 0.6 }
      );
      this.hintObserver.observe(this.scroller);
    }

    playHint() {
      const isScrollable = this.scroller.scrollWidth > this.scroller.clientWidth;
      if (!this.isMobile() || !isScrollable) return;

      this.hintAnimation = this.list.animate(
        [
          { transform: 'translateX(0)' },
          { transform: `translateX(${HINT_DISTANCE}px)`, offset: 0.4 },
          { transform: 'translateX(0)' },
        ],
        { duration: HINT_DURATION, easing: 'ease-in-out' }
      );
    }
  }

  customElements.define('pdp-ingredient-spotlight', PdpIngredientSpotlight);
})();
